import { useEffect, useState } from 'react';

// Simple reactive variable implementation to replace Apollo's makeVar/useReactiveVar
type Listener<T> = (newValue: T) => void;

// Callable reactive variable function type - matches Apollo's API exactly
type ReactiveVarFunction<T> = {
  (): T; // Call with no args to get value
  (newValue: T): T; // Call with arg to set value
  getValue(): T; // Explicit getter for internal use
  setValue(newValue: T): void; // Explicit setter for internal use
  subscribe(listener: Listener<T>): () => void; // For useReactiveVar hook
  onNextChange(listener: Listener<T>): () => void; // Listen for next single change
};

class ReactiveVarImpl<T> {
  private value: T;
  private listeners: Set<Listener<T>> = new Set();
  private nextChangeListeners: Set<Listener<T>> = new Set();

  constructor(initialValue: T) {
    this.value = initialValue;
  }

  getValue(): T {
    return this.value;
  }

  setValue(newValue: T): void {
    this.value = newValue;

    // Notify regular subscribers
    this.listeners.forEach(listener => listener(newValue));

    // Notify and remove next-change listeners (they only fire once)
    this.nextChangeListeners.forEach(listener => listener(newValue));
    this.nextChangeListeners.clear();
  }

  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  onNextChange(listener: Listener<T>): () => void {
    this.nextChangeListeners.add(listener);
    return () => {
      this.nextChangeListeners.delete(listener);
    };
  }
}

// Factory function to create reactive variables (replaces makeVar)
export function makeVar<T>(initialValue: T): ReactiveVarFunction<T> {
  const impl = new ReactiveVarImpl(initialValue);

  // Create the callable function that matches Apollo's API
  const reactiveVar = ((...args: [T] | []) => {
    if (args.length === 0) {
      // Called with no arguments - return current value
      return impl.getValue();
    } else {
      // Called with argument - set value and return it
      const [newValue] = args;
      impl.setValue(newValue);
      return newValue;
    }
  }) as ReactiveVarFunction<T>;

  // Add the additional methods for internal use
  reactiveVar.getValue = () => impl.getValue();
  reactiveVar.setValue = (newValue: T) => impl.setValue(newValue);
  reactiveVar.subscribe = (listener: Listener<T>) => impl.subscribe(listener);
  reactiveVar.onNextChange = (listener: Listener<T>) => impl.onNextChange(listener);

  return reactiveVar;
}

// Hook to use reactive variables in React components (replaces useReactiveVar)
export function useReactiveVar<T>(reactiveVar: ReactiveVarFunction<T>): T {
  const [value, setValue] = useState(reactiveVar.getValue());

  useEffect(() => {
    const unsubscribe = reactiveVar.subscribe(setValue);
    // Sync with current value in case it changed since initial render
    setValue(reactiveVar.getValue());
    return unsubscribe;
  }, [reactiveVar]);

  return value;
}
